import { useState, useEffect, useMemo } from 'react';
import { useUpdateSubscriptionMutation, usePrecioDesdePlazasQuery, useRecalcularDesdePlazasMutation } from '../../services/hooks/useSuscripciones';
import { Suscripcion } from '../../types/index';
import { Loader2, X, Search } from 'lucide-react';
import { toast } from 'react-toastify';
import { queryClient } from '../../main';
import { useClientsWithoutBeneficiaryQuery, useConfigurationsQuery, useNivelesQuery, useSafeMounted } from '../../services/hooks';
import { DesglosePrecio } from '../suscripciones/PrecioSuscripcion';
import { ClienteSinBeneficiario } from '../../types/clientes';
import { useRole } from '../../hooks/useRole';
import { validateIban, formatIban } from '../../utils/ibanValidation';
import { SUSCRIPCION_DOMICILIACION, SUSCRIPCION_OTROS_PAGOS, etiquetaSuscripcion } from '../../utils/suscripciones';

interface EditarSuscripcionProps {
    suscripcion: Suscripcion;
    clientId: string;
    onClose: () => void;
    // Callback opcional para invalidar queries adicionales cuando se usa desde contextos específicos
    // Por ejemplo: desde SepaClientsList necesita invalidar queries SEPA además de las queries de cliente
    // En ClienteDetalle no es necesario ya que solo maneja queries de cliente
    onSuscripcionUpdated?: () => void;
}

const TIPOS_PAGO_POR_SUSCRIPCION = {
    "pilates-domiciliacion": ['Domiciliaciones', 'Pagador - Cliente', 'Pagador - No Cliente'],
    "pilates-otros-pagos": ['Efectivo', 'Tarjeta']
};

const EditarSuscripcion: React.FC<EditarSuscripcionProps> = ({ 
    suscripcion, 
    clientId,
    onClose,
    onSuscripcionUpdated // Callback opcional para contextos específicos (ej: SepaClientsList)
}) => {

    const isMounted = useSafeMounted();
    // Si el componente está desmontado, no renderizar nada
    if (!isMounted.current) return null;

    const { isAdmin, isSuperAdmin } = useRole();
    const { data: niveles } = useNivelesQuery();

    // Cargar configuraciones para obtener las tarifas por nivel
    const { data: configurations, isLoading: isLoadingConfig, error: configError } = useConfigurationsQuery();

    const [formData, setFormData] = useState<Partial<Suscripcion>>({});

    // Precio a mano: solo admin, y solo con "precio fijo" marcado. Sin él, un
    // importe escrito a mano se perdería en el siguiente cambio de plazas.
    const puedeFijarPrecio = isAdmin || isSuperAdmin;
    const puedeEditarPrecio = puedeFijarPrecio && !!formData.precioFijo;

    // Niveles con varias tarifas (máquina fundador / general): un selector por
    // cada grupo de opciones. La configuración solo la lee un admin; para un
    // operador la lista llega vacía y no se muestra nada.
    const familiasTarifa = useMemo(() => {
        const tarifasNombradas = configurations?.[0]?.tarifasNombradas || [];
        const porClave = new Map<string, { clave: string; etiqueta: string; opciones: { _id: string; nombre: string; cerrada?: boolean }[] }>();
        const nivelesCubiertos = [...new Set(tarifasNombradas.flatMap((t) => t.niveles))];
        for (const nivelId of nivelesCubiertos) {
            const opciones = tarifasNombradas.filter((t) => t.niveles.includes(nivelId))
                .map((t) => ({ _id: t._id, nombre: t.nombre, cerrada: !!t.cerradaAltas }));
            if (opciones.length < 2) continue;
            const clave = opciones.map((o) => o._id).sort().join('|');
            const nombreNivel = niveles?.find((n) => n._id === nivelId)?.nombre || 'este nivel';
            const existente = porClave.get(clave);
            if (existente) existente.etiqueta = `${existente.etiqueta}, ${nombreNivel}`;
            else porClave.set(clave, { clave, etiqueta: nombreNivel, opciones });
        }
        return [...porClave.values()];
    }, [configurations, niveles]);

    // Vista previa del importe con las plazas actuales y, si se ha cambiado en
    // el formulario, con la tarifa nueva. Lo calcula el back.
    const tarifasCambiadas = JSON.stringify([...(suscripcion.tarifasAsignadas || [])].sort())
        !== JSON.stringify([...(formData.tarifasAsignadas || [])].sort());
    const esIlimitada = suscripcion.frecuencia === 'Ilimitada';
    const previewPrecio = usePrecioDesdePlazasQuery({
        suscripcionId: suscripcion._id,
        tarifasAsignadas: tarifasCambiadas ? formData.tarifasAsignadas : undefined,
        enabled: !formData.precioFijo && !suscripcion.precioFijo && !esIlimitada,
    });
    const recalcularMutation = useRecalcularDesdePlazasMutation();
    const cambioPrecio = previewPrecio.data?.cambios[0];

    const aplicarRecalculo = async () => {
        try {
            const r = await recalcularMutation.mutateAsync({ dryRun: false, suscripcionId: suscripcion._id });
            const nuevo = r.cambios[0];
            if (nuevo) {
                setFormData((prev) => ({ ...prev, importeSuscripcion: nuevo.despues, frecuencia: nuevo.frecuencia }));
                toast.success(`Importe actualizado: ${nuevo.antes} € → ${nuevo.despues} €`);
            }
            previewPrecio.refetch();
            onSuscripcionUpdated?.();
        } catch { /* el toast lo pone el hook */ }
    };

    // Elegir una tarifa de un grupo quita las otras del mismo grupo. Una
    // cerrada a nuevas altas que el cliente no tenía pide confirmación.
    const cambiarTarifa = (delGrupo: string[], tarifaId: string, opcion?: { nombre: string; cerrada?: boolean }) => {
        if (opcion?.cerrada && !(suscripcion.tarifasAsignadas || []).includes(tarifaId)
            && !window.confirm(`"${opcion.nombre}" está cerrada a nuevas altas. ¿Asignarla a este cliente de todos modos?`)) {
            return;
        }
        setFormData((prev) => ({
            ...prev,
            tarifasAsignadas: [...(prev.tarifasAsignadas || []).filter((id) => !delGrupo.includes(id)), ...(tarifaId ? [tarifaId] : [])],
        }));
    };
    const [searchTerm, setSearchTerm] = useState('');
    const [showClientSearch, setShowClientSearch] = useState(false);
    
    // Estados para manejar los datos del pagador externo (Pagador - No Cliente)
    const [pagadorExterno, setPagadorExterno] = useState({
        nombre: '',
        DNI: '',
        IBAN: '',
        telefono: '',
        email: ''
    });
    
    // Estados para validación IBAN
    const [ibanErrors, setIbanErrors] = useState({
        domiciliacion: '',
        pagadorExterno: ''
    });
    
    // Consultar lista de clientes para el buscador de "Pagador - Cliente"
    const { data: clientes, isLoading: isLoadingClientes } = useClientsWithoutBeneficiaryQuery({
        enabled: showClientSearch && formData.tipoPago === 'Pagador - Cliente',
    });
    
    const updateSubscriptionMutation = useUpdateSubscriptionMutation();
    
    // Inicializar el formulario con los datos de la suscripción
    useEffect(() => {
        setFormData({
            nombreSuscripcion: suscripcion.nombreSuscripcion,
            importeSuscripcion: suscripcion.importeSuscripcion,
            frecuencia: suscripcion.frecuencia,
            tipoPago: suscripcion.tipoPago,
            descuento: suscripcion.descuento || 0,
            activo: suscripcion.activo,
            observaciones: suscripcion.observaciones,
            iban: suscripcion.iban,
            pagador_id: suscripcion.pagador_id,
            beneficiario: suscripcion.beneficiario,
            tarifasAsignadas: suscripcion.tarifasAsignadas || [],
            precioFijo: !!suscripcion.precioFijo,
        });
        
        // Si tiene pagador externo, inicializar esos datos
        if (suscripcion.pagador) {
            setPagadorExterno({
                nombre: suscripcion.pagador.nombre || '',
                DNI: suscripcion.pagador.DNI || '',
                IBAN: suscripcion.pagador.IBAN || '',
                telefono: suscripcion.pagador.telefono || '',
                email: suscripcion.pagador.email || ''
            });
        }
    }, [suscripcion]);

    // Manejar cambio en el tipo de pago
    const handleTipoPagoChange = (value: string) => {
        setFormData({...formData, tipoPago: value});
        
        // Si cambia a "Pagador - Cliente", mostrar el buscador
        if (value === 'Pagador - Cliente') {
            setShowClientSearch(true);
        } else {
            setShowClientSearch(false);
        }
    };


    // Manejar cambio en el tipo de suscripción
    // Esta función se encarga de actualizar el tipo de suscripción y ajustar el tipo de pago si es necesario
    const handleSuscripcionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const newValue = e.target.value;
        const updates: Partial<Suscripcion> = {
            nombreSuscripcion: newValue
        };
        
        // Obtener las opciones de pago válidas para este tipo de suscripción
        const opcionesPagoValidas = TIPOS_PAGO_POR_SUSCRIPCION[newValue as keyof typeof TIPOS_PAGO_POR_SUSCRIPCION] || [];
        
        // Si el tipo de pago actual no es válido para la nueva suscripción, asignar el primero disponible
        if (formData.tipoPago && !opcionesPagoValidas.includes(formData.tipoPago)) {
            updates.tipoPago = opcionesPagoValidas[0] || '';
        }
        
        // Actualizar el estado con los cambios
        setFormData({...formData, ...updates});
    };

    
    // Determinar qué opciones de pago mostrar según el tipo de suscripción seleccionado
    const opcionesPagoDisponibles = formData.nombreSuscripcion 
        ? (TIPOS_PAGO_POR_SUSCRIPCION[formData.nombreSuscripcion as keyof typeof TIPOS_PAGO_POR_SUSCRIPCION] || [])
        : [];

    // Funciones para manejar validación IBAN
    const handleIbanChange = (value: string, field: 'domiciliacion' | 'pagadorExterno') => {
        // Formatear visualmente el IBAN mientras se escribe
        const formatted = formatIban(value);
        
        // Actualizar el estado correspondiente
        if (field === 'domiciliacion') {
            setFormData({...formData, iban: formatted});
        } else {
            setPagadorExterno({...pagadorExterno, IBAN: formatted});
        }
    };

    const validateIbanField = (value: string, field: 'domiciliacion' | 'pagadorExterno') => {
        const validation = validateIban(value);
        
        setIbanErrors(prev => ({
            ...prev,
            [field]: validation.isValid ? '' : (validation.error || 'Hay un error en el formato del IBAN')
        }));
        
        return validation.isValid;
    };

    
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        // Validar IBANs antes de enviar
        let formIsValid = true;
        
        // Validar IBAN de domiciliación si está presente
        if (formData.tipoPago === 'Domiciliaciones' && formData.iban) {
            const isValid = validateIbanField(formData.iban, 'domiciliacion');
            if (!isValid) formIsValid = false;
        }
        
        // Validar IBAN de pagador externo si está presente
        if (formData.tipoPago === 'Pagador - No Cliente' && pagadorExterno.IBAN) {
            const isValid = validateIbanField(pagadorExterno.IBAN, 'pagadorExterno');
            if (!isValid) formIsValid = false;
        }
        
        // Si hay errores de validación, no enviar
        if (!formIsValid) {
            toast.error('Por favor, corrige los errores en el formulario');
            return;
        }
        
        try {
            // Crear copia del formData para modificar
            const dataToSubmit = {...formData};
            // La frecuencia la mantiene el back a partir de las plazas: no se
            // reenvía la que se cargó al abrir, que podría estar desfasada.
            delete dataToSubmit.frecuencia;
            
            
            // Si es "Pagador - No Cliente", añadir los datos del pagador externo
            if (formData.tipoPago === 'Pagador - No Cliente') {
                // Limpiar IBAN del pagador externo antes de enviar
                const pagadorLimpio = {...pagadorExterno};
                if (pagadorLimpio.IBAN) {
                    const validation = validateIban(pagadorLimpio.IBAN);
                    pagadorLimpio.IBAN = validation.clean || '';
                }
                dataToSubmit.pagador = pagadorLimpio;
            }
            
            // Si cambia de "Pagador - No Cliente" a otro tipo, eliminar pagador
            if (formData.tipoPago !== 'Pagador - No Cliente') {
                dataToSubmit.pagador = undefined;
            }
            
            // Si cambia de "Pagador - Cliente" a otro tipo, eliminar pagador_id
            if (formData.tipoPago !== 'Pagador - Cliente') {
                dataToSubmit.pagador_id = undefined;
            }
            
            // Si no es domiciliación, eliminar IBAN
            if (formData.tipoPago !== 'Domiciliaciones') {
                dataToSubmit.iban = '';
            } else {
                // Si es domiciliación, limpiar IBAN antes de enviar
                if (dataToSubmit.iban) {
                    const validation = validateIban(dataToSubmit.iban);
                    dataToSubmit.iban = validation.clean || '';
                }
            }
            console.log('data to submit', dataToSubmit);
            await updateSubscriptionMutation.mutateAsync({ 
                id: suscripcion._id, 
                data: dataToSubmit 
            });
            
            // Invalidar consultas para refrescar los datos
            queryClient.invalidateQueries({ 
                queryKey: ['client', clientId] 
            });

            // Callback opcional para invalidar queries adicionales en contextos específicos
            // Ejemplo: SepaClientsList necesita invalidar ['sepa-clients'] además de ['client']
            // ClienteDetalle no necesita esto ya que solo maneja queries de cliente
            if (onSuscripcionUpdated) {
                onSuscripcionUpdated();
            }

            // toast.success('Suscripción actualizada correctamente');
            onClose();
        } catch (error) {
            console.error(error);
            toast.error('Error al actualizar la suscripción');
        }
    };
    
    // Filtrar clientes según el término de búsqueda
    const filteredClientes = clientes?.filter(cliente => 
        cliente.nombre.toLowerCase().includes(searchTerm.toLowerCase())
    ) || [];
    
    // Seleccionar cliente como pagador
    const selectClienteAsPagador = (clienteNombre: string, subscriptionId: string) => {
        setFormData({...formData, pagador_id: subscriptionId});
        setSearchTerm(clienteNombre);
        setShowClientSearch(false);
        toast.info(`Cliente "${clienteNombre}" seleccionado como pagador`);
    };

    const hasBeneficiario = !!formData.beneficiario;
    
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-bold">Editar suscripción</h3>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
                        <X size={20} />
                    </button>
                </div>
                
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Nombre de suscripción</label>
                        <select
                            value={formData.nombreSuscripcion || ''}
                            onChange={handleSuscripcionChange}
                            className={`mt-1 block w-full border rounded-md shadow-sm py-2 px-3 ${
                                hasBeneficiario
                                ? 'bg-gray-100 text-gray-500 border-gray-200 cursor-not-allowed'
                                : 'border-gray-300'
                            }`}
                            required
                            disabled={hasBeneficiario}
                        >
                            <option value="">Seleccione un tipo de suscripción</option>
                            {/* El `value` es el identificador guardado en Mongo y no
                                se toca; solo se generaliza la etiqueta. */}
                            <option value={SUSCRIPCION_DOMICILIACION}>{etiquetaSuscripcion(SUSCRIPCION_DOMICILIACION)}</option>
                            <option value={SUSCRIPCION_OTROS_PAGOS}>{etiquetaSuscripcion(SUSCRIPCION_OTROS_PAGOS)}</option>
                        </select>
                        {hasBeneficiario && (
                            <p className="mt-1 text-xs text-gray-500 italic">
                                Este campo no se puede cambiar cuando hay un beneficiario.
                            </p>
                        )}
                    </div>
                    
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Tipo de pago</label>
                        <select
                            value={formData.tipoPago || ''}
                            onChange={(e) => handleTipoPagoChange(e.target.value)}
                            className={`mt-1 block w-full border rounded-md shadow-sm py-2 px-3 ${
                                hasBeneficiario
                                ? 'bg-gray-100 text-gray-500 border-gray-200 cursor-not-allowed'
                                : 'border-gray-300'
                            }`}
                            disabled={hasBeneficiario}
                        >
                            <option value="">Seleccione un tipo de pago</option>
                            {opcionesPagoDisponibles.map((tipo) => (
                                <option key={tipo} value={tipo}>{tipo}</option>
                            ))}
                        </select>
                        {hasBeneficiario && (
                            <p className="mt-1 text-xs text-gray-500 italic">
                                Este campo no se puede cambiar cuando hay un beneficiario.
                            </p>
                        )}
                    </div>
                    
                    {/* Campos específicos según el tipo de pago */}
                    {formData.tipoPago === 'Domiciliaciones' && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700">IBAN</label>
                            <input
                                type="text"
                                value={formData.iban || ''}
                                onChange={(e) => handleIbanChange(e.target.value, 'domiciliacion')}
                                onBlur={(e) => validateIbanField(e.target.value, 'domiciliacion')}
                                className={`mt-1 block w-full border rounded-md shadow-sm py-2 px-3 ${
                                    ibanErrors.domiciliacion 
                                        ? 'border-red-300 focus:border-red-500 focus:ring-red-500' 
                                        : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                                }`}
                                placeholder="ES66 2100 0418 4012 3456 7891"
                            />
                            {ibanErrors.domiciliacion && (
                                <p className="mt-1 text-sm text-red-600">
                                    {ibanErrors.domiciliacion}
                                </p>
                            )}
                        </div>
                    )}
                    
                    {/* Pagador - Cliente: Buscador de clientes */}
                    {formData.tipoPago === 'Pagador - Cliente' && (
                        <div className="relative">
                            <label className="block text-sm font-medium text-gray-700">Buscar cliente pagador</label>
                            <div className="flex">
                                <div className="relative flex-1">
                                    <input
                                        type="text"
                                        value={searchTerm}
                                        onChange={(e) => {
                                            setSearchTerm(e.target.value);
                                            setShowClientSearch(true);
                                        }}
                                        onClick={() => setShowClientSearch(true)}
                                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 pr-10"
                                        placeholder="Buscar por nombre, DNI o teléfono"
                                    />
                                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 mt-1">
                                        <Search size={16} className="text-gray-400" />
                                    </div>
                                </div>
                            </div>
                            
                            {/* Resultados de búsqueda */}
                            {showClientSearch && (
                                <div className="absolute z-10 mt-1 w-full bg-white shadow-lg rounded-md py-1 max-h-60 overflow-auto border">
                                    {isLoadingClientes ? (
                                        <div className="flex items-center justify-center p-4">
                                            <Loader2 size={20} className="animate-spin mr-2" />
                                            <span>Cargando clientes...</span>
                                        </div>
                                    ) : filteredClientes.length > 0 ? (
                                        filteredClientes.map((cliente: ClienteSinBeneficiario) => (
                                            <div
                                                key={cliente.client_id}
                                                className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                                                onClick={() => selectClienteAsPagador(cliente.nombre, cliente.subscription_id)}
                                            >
                                                <div className="font-medium">{cliente.nombre}</div>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="px-4 py-2 text-gray-500">No se encontraron clientes</div>
                                    )}
                                </div>
                            )}
                            
                            {/* Mensaje de cliente seleccionado */}
                            {formData.pagador_id && !showClientSearch && (
                                <div className="mt-2 p-2 bg-green-50 text-green-800 rounded-md text-sm">
                                    Cliente pagador seleccionado. 
                                    <button 
                                        type="button"
                                        onClick={() => setShowClientSearch(true)}
                                        className="ml-2 text-green-600 hover:text-green-800 underline"
                                    >
                                        Cambiar
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                    
                    {/* Pagador - No Cliente: Formulario para datos del pagador externo */}
                    {formData.tipoPago === 'Pagador - No Cliente' && (
                        <div className="space-y-3 p-3 border border-blue-200 bg-blue-50 rounded-md">
                            <h4 className="font-medium text-blue-800">Datos del pagador externo</h4>
                            
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Nombre completo</label>
                                <input
                                    type="text"
                                    value={pagadorExterno.nombre}
                                    onChange={(e) => setPagadorExterno({...pagadorExterno, nombre: e.target.value})}
                                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3"
                                    required
                                />
                            </div>
                            
                            <div>
                                <label className="block text-sm font-medium text-gray-700">DNI</label>
                                <input
                                    type="text"
                                    value={pagadorExterno.DNI}
                                    onChange={(e) => setPagadorExterno({...pagadorExterno, DNI: e.target.value})}
                                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3"
                                    required
                                />
                            </div>
                            
                            <div>
                                <label className="block text-sm font-medium text-gray-700">IBAN</label>
                                <input
                                    type="text"
                                    value={pagadorExterno.IBAN}
                                    onChange={(e) => handleIbanChange(e.target.value, 'pagadorExterno')}
                                    onBlur={(e) => validateIbanField(e.target.value, 'pagadorExterno')}
                                    className={`mt-1 block w-full border rounded-md shadow-sm py-2 px-3 ${
                                        ibanErrors.pagadorExterno 
                                            ? 'border-red-300 focus:border-red-500 focus:ring-red-500' 
                                            : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                                    }`}
                                    placeholder="ES66 2100 0418 4012 3456 7891"
                                    required
                                />
                                {ibanErrors.pagadorExterno && (
                                    <p className="mt-1 text-sm text-red-600">
                                        {ibanErrors.pagadorExterno}
                                    </p>
                                )}
                            </div>
                            
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Teléfono</label>
                                <input
                                    type="tel"
                                    value={pagadorExterno.telefono}
                                    onChange={(e) => setPagadorExterno({...pagadorExterno, telefono: e.target.value})}
                                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3"
                                    required
                                />
                            </div>
                            
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Email (opcional)</label>
                                <input
                                    type="email"
                                    value={pagadorExterno.email}
                                    onChange={(e) => setPagadorExterno({...pagadorExterno, email: e.target.value})}
                                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3"
                                />
                            </div>
                        </div>
                    )}
                    
                    {/* Tarifas por nivel: la elegida en cada nivel que tiene varias */}
                    {familiasTarifa.length > 0 && (
                        <div className="space-y-2">
                            {familiasTarifa.map((familia) => {
                                const actual = familia.opciones.find((o) => (formData.tarifasAsignadas || []).includes(o._id))?._id || '';
                                // "Sin elegir" solo si aún no tenía tarifa aquí: volver a ese
                                // estado no lleva a la tarifa general, deja al cliente sin precio
                                // calculable y el siguiente cambio de plazas pediría elegir.
                                const yaTenia = familia.opciones.some((o) => (suscripcion.tarifasAsignadas || []).includes(o._id));
                                return (
                                    <div key={familia.clave}>
                                        <label className="block text-sm font-medium text-gray-700">Tarifa de {familia.etiqueta}</label>
                                        <select
                                            value={actual}
                                            onChange={(e) => cambiarTarifa(
                                                familia.opciones.map((o) => o._id),
                                                e.target.value,
                                                familia.opciones.find((o) => o._id === e.target.value),
                                            )}
                                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3"
                                        >
                                            {!yaTenia && <option value="">Sin elegir</option>}
                                            {familia.opciones.map((o) => (
                                                <option key={o._id} value={o._id}>{o.nombre}{o.cerrada ? ' (cerrada a nuevas altas)' : ''}</option>
                                            ))}
                                        </select>
                                    </div>
                                );
                            })}
                            <p className="text-xs text-gray-500">Al cambiar de tarifa, el importe se recalcula con las plazas del cliente al guardar.</p>
                        </div>
                    )}

                    {suscripcion.desglose?.length ? (
                        <div>
                            <span className="block text-sm font-medium text-gray-700 mb-1">Desglose del importe</span>
                            <DesglosePrecio desglose={suscripcion.desglose} total={suscripcion.importeSuscripcion} />
                        </div>
                    ) : null}

                    {puedeFijarPrecio && (
                        <label className="flex items-start gap-2 text-sm text-gray-700">
                            <input
                                type="checkbox"
                                checked={!!formData.precioFijo}
                                onChange={(e) => setFormData((prev) => ({ ...prev, precioFijo: e.target.checked }))}
                                className="mt-0.5"
                            />
                            <span>
                                Precio fijo
                                <span className="block text-xs text-gray-500">
                                    Permite poner el importe a mano. No cambiará al añadir o quitar plazas ni al actualizar
                                    tarifas. Al desmarcarlo, se recalcula con las plazas del cliente.
                                </span>
                            </span>
                        </label>
                    )}

                    <div>
                        <label className="block text-sm font-medium text-gray-700">Importe (€)</label>
                        <input
                            type="number"
                            value={formData.importeSuscripcion || ''}
                            onChange={(e) => setFormData({...formData, importeSuscripcion: Number(e.target.value)})}
                            className={`mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 ${puedeEditarPrecio ? '' : 'bg-gray-50'}`}
                            readOnly={!puedeEditarPrecio}
                            required
                        />
                        {!puedeEditarPrecio && (
                            <p className="mt-1 text-xs text-gray-500">
                                {puedeFijarPrecio
                                    ? 'Se calcula con las plazas y las tarifas. Marca "Precio fijo" para ponerlo a mano.'
                                    : 'Se calcula con las plazas y las tarifas.'}
                            </p>
                        )}

                        {/* Qué daría el cálculo con sus plazas y tarifas de ahora */}
                        {suscripcion.precioFijo && !formData.precioFijo && (
                            <p className="mt-2 text-xs text-amber-700">Al guardar sin precio fijo, el importe se recalcula con sus plazas.</p>
                        )}
                        {previewPrecio.data && !formData.precioFijo && (
                            <div className="mt-2 rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm space-y-1">
                                {cambioPrecio ? (
                                    <>
                                        <p>
                                            Con sus plazas y tarifas: {cambioPrecio.antes} € → <strong>{cambioPrecio.despues} €</strong>
                                            <span className="text-gray-500"> ({cambioPrecio.frecuencia})</span>
                                        </p>
                                        <DesglosePrecio desglose={cambioPrecio.desglose} total={cambioPrecio.despues} />
                                        {tarifasCambiadas ? (
                                            <p className="text-xs text-gray-500">Se aplicará al guardar.</p>
                                        ) : (
                                            <button
                                                type="button"
                                                onClick={aplicarRecalculo}
                                                disabled={recalcularMutation.isPending}
                                                className="mt-1 px-3 py-1 rounded bg-brand-primary hover:brightness-90 text-white text-sm disabled:opacity-50"
                                            >
                                                {recalcularMutation.isPending ? 'Actualizando…' : 'Recalcular importe'}
                                            </button>
                                        )}
                                    </>
                                ) : previewPrecio.data.faltaElegir.length ? (
                                    <p className="text-amber-700">Elige la tarifa de {previewPrecio.data.faltaElegir[0].opciones.join(' / ')} para calcular el importe.</p>
                                ) : previewPrecio.data.sinPrecio.length ? (
                                    <p className="text-red-600">{previewPrecio.data.sinPrecio[0].motivo}</p>
                                ) : previewPrecio.data.omitidas.sinPlazas ? (
                                    <p className="text-gray-600">Sin plazas activas: el importe no se puede calcular.</p>
                                ) : (
                                    <p className="text-gray-600">El importe está al día con sus plazas y tarifas.</p>
                                )}
                            </div>
                        )}
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700">Descuento (€)</label>
                        <input
                            type="number"
                            value={formData.descuento || 0}
                            onChange={(e) => setFormData({...formData, descuento: Number(e.target.value)})}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3"
                        />
                    </div>
                    
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Observaciones</label>
                        <textarea
                            value={formData.observaciones || ''}
                            onChange={(e) => setFormData({...formData, observaciones: e.target.value})}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3"
                            rows={3}
                        />
                    </div>
                    
                    
                    <div className="flex justify-end space-x-2 pt-4">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            disabled={
                                updateSubscriptionMutation.isPending || 
                                ibanErrors.domiciliacion !== '' || 
                                ibanErrors.pagadorExterno !== ''
                            }
                            className={`px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white flex items-center ${
                                (ibanErrors.domiciliacion !== '' || ibanErrors.pagadorExterno !== '') 
                                    ? 'bg-gray-400 cursor-not-allowed' 
                                    : 'bg-blue-600 hover:bg-blue-700'
                            }`}
                        >
                            {updateSubscriptionMutation.isPending && <Loader2 size={16} className="animate-spin mr-2" />}
                            Guardar cambios
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EditarSuscripcion;